// Where ads may show, one rule for every kind (Adsterra and direct slots, site/js/ad-slots.js; Google AdSense Auto ads)
// and every place that draws a page: the Worker (worker/src/seo.js, block.js), the app (site/js/app.js) and the public
// meeting page (dashboard-src/main.tsx, ads/AdSlot.tsx).
//
//   NO_ADS         pages that never show an ad and never load an ad script, even when the app navigates there from a
//                  page that did: the body gets .no-ads, which hides any slot or anything AdSense placed (style.css).
//   NO_AD_REGIONS  no ads at all for readers in the EU/EEA, the UK and Switzerland: AdSense needs a certified consent
//                  banner there, and the site has none. The Worker decides from request.cf.country (GET /ads/region,
//                  never cached); the page asks it before loading anything, so the HTML is the same for every country
//                  and caches as before.
//   syncAds(path)  after each page is drawn: marks a no-ads page, or (where the reader may see ads) loads AdSense Auto ads
//                  once, after the page has loaded and the browser is idle, or at the reader's first tap, scroll or key.

export const ADSENSE_CLIENT = 'ca-pub-3037385335943122';
export const ADSENSE_SCRIPT = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${ADSENSE_CLIENT}`;

export const NO_ADS = /^\/(?:elections\/2027(?:\/|$)|candidate(?:\/|$)|signin$|account$|alerts(?:\/|$)|about$|find$|donate$|pricing$|for(?:\/|$)|dashboard(?:\/|$)|d\/)/;
const clean = (path) => String(path || '/').toLowerCase().replace(/\/+$/, '') || '/';
/** Whether a page never shows ads. */
export const noAds = (path) => NO_ADS.test(clean(path));

// EU and EEA members (with the outermost regions Cloudflare reports on their own), the UK and Switzerland.
export const NO_AD_REGIONS = new Set(['AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE',
  'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE', 'IS', 'LI', 'NO', 'GB', 'CH',
  'AX', 'GF', 'GP', 'MQ', 'RE', 'YT', 'MF', 'GI']);
/** Whether a reader in `country` (request.cf.country) may see ads. Unknown (none, XX, T1 = Tor): no. */
export const adsForCountry = (country) => typeof country === 'string' && /^[A-Z]{2}$/.test(country) && !['XX', 'T1'].includes(country) && !NO_AD_REGIONS.has(country);

// ---------- in the browser ----------
let asking = null;
/**
 * Whether this reader may see ads (GET /ads/region), kept for the tab's session in sessionStorage (in memory while
 * asking, or for good where storage is blocked). False when it can't tell.
 */
export function adsAllowedHere() {
  let kept = null;
  try { kept = sessionStorage.getItem('cp-ads'); } catch { /* blocked */ }
  if (kept === '1' || kept === '0') return Promise.resolve(kept === '1');
  return (asking ||= fetch('/ads/region', { credentials: 'omit' }).then((r) => (r.ok ? r.json() : { ads: false })).then((d) => {
    try { sessionStorage.setItem('cp-ads', d.ads ? '1' : '0'); asking = null; } catch { /* blocked */ }
    return !!d.ads;
  }).catch(() => false));
}

// AdSense Auto ads, once per visit, without competing with the page's first paint.
let autoAds = false;
function loadAutoAds() {
  if (autoAds) return;
  autoAds = true;
  if (document.querySelector('script[src^="https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js"]')) return;
  let done = false;
  const go = () => {
    if (done) return;
    done = true;
    const s = Object.assign(document.createElement('script'), { async: true, src: ADSENSE_SCRIPT, crossOrigin: 'anonymous' });
    document.head.appendChild(s);
  };
  for (const e of ['pointerdown', 'keydown', 'touchstart', 'scroll']) addEventListener(e, go, { once: true, passive: true, capture: true });
  const idle = () => (window.requestIdleCallback || setTimeout)(go, { timeout: 3000 });
  if (document.readyState === 'complete') idle(); else addEventListener('load', idle, { once: true });
}

/**
 * After a page is drawn (and on every in-app navigation). Resolves true where this page may show ads to this reader;
 * false on a no-ads page (also one the reader moved on to while the answer came), or for a reader in those regions.
 */
export async function syncAds(path = location.pathname) {
  const body = document.body;
  if (noAds(path)) { body.classList.add('no-ads'); return false; }
  const ok = await adsAllowedHere();
  if (noAds(location.pathname)) return false;   // the reader moved on to a no-ads page meanwhile; that page decided
  body.classList.toggle('no-ads', !ok);
  if (ok) loadAutoAds();
  return ok;
}
